import { getcmsPageByUrl } from "@/services/cms";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import React, { useEffect } from "react";
import { setupCmsEvents } from "@/utils/cms";
import { buildCmsHtml } from "@/utils/cms/buildCmsHtml";
import BreadCrumb from "@/components/Reusable/Breadcrumb";
import SEO from "@/components/seo";
import { handshakeCookieName } from "@/constants";
import { localeHandShake } from "@/services/token/handshake";
import { useRouter } from "next/router";
import SafetyProgram from "@/components/SafetyProgram/SafetyProgram";

// The Tawuniya T&C tab buttons carry their default-state colour directly in the
// Magento HTML (attachTawuniyaTermsTabEvents only reruns TW_TAB_ACTIVE on click,
// never on mount), so campaign theming has to touch this string too. Exact-match
// only, scoped to this one page — never folded into buildCmsHtml, which every
// other CMS page also uses.
const themeTawuniyaTabs = (html) =>
  html
    ?.replaceAll(
      "bg-[#00269a] font-bold text-white",
      "bg-[#00269a] campaign:bg-campaignAccent font-bold text-white"
    )
    ?.replaceAll(
      "hover:text-[#00269a]",
      "hover:text-[#00269a] campaign:hover:text-campaignAccent"
    );

// Magento's cmsPage/search returns every page matching the identifier across all
// store views (it does not scope results by the request store), so a page that is
// localized as a separate per-store record surfaces as multiple items with the
// same identifier. Pick the one whose content language matches the active locale
// (by Arabic-character ratio); fall back to the single/first item.
const arabicRatio = (text = "") =>
  (text.match(/[؀-ۿ]/g) || []).length / (text.length || 1);

const pickCmsItemForLocale = (items, locale) => {
  if (!Array.isArray(items) || items.length === 0) return null;
  if (items.length === 1) return items[0];
  const ranked = [...items].sort(
    (a, b) => arabicRatio(b?.content) - arabicRatio(a?.content)
  );
  return locale === "ar" ? ranked[0] : ranked[ranked.length - 1];
};

const ContentPage = ({ content, safetyProgram, tawuniyaTerms }) => {
  const { locale } = useRouter();

  const BreadCrumbItems = [
    {
      name: content?.title,
      url: "/" + content?.identifier,
      active: true,
      key: content?.identifier,
    },
  ];

  useEffect(() => {
    if (!content?.content) return;
    const cleanup = setupCmsEvents(locale);
    return cleanup;
  }, [content, locale]);

  if (safetyProgram) {
    return (
      <div className="overflow-x-hidden bg-white almaneaCMSPage">
        <SEO
          title={
            locale === "ar"
              ? "شروط وأحكام برنامج الضمان"
              : "Terms and Conditions of the Warranty Program"
          }
        />
        <SafetyProgram locale={locale} />
      </div>
    );
  }

  if (tawuniyaTerms) {
    return (
      <div className="overflow-x-hidden bg-white almaneaCMSPage">
        <SEO
          title={
            locale === "ar"
              ? "شروط وأحكام المنيع مدعوم من التعاونية"
              : "Al Manea Supported by Tawuniya Terms and Conditions"
          }
        />
        {/* BE-authored HTML carries its own layout, lang switcher and tabs;
            rendered full-width (no breadcrumb) and wired by setupCmsEvents. */}
        <div
          dangerouslySetInnerHTML={{
            __html: themeTawuniyaTabs(buildCmsHtml(content?.content)),
          }}
        />
      </div>
    );
  }

  return (
    <div className="overflow-x-hidden bg-white almaneaCMSPage">
      <SEO
        title={content?.meta_title || content?.title || ""}
        description={content?.meta_description || ""}
        keywords={content?.meta_keyword || ""}
        image={content?._media_?.image?.at(0)?.image || ""}
      />
      <BreadCrumb items={BreadCrumbItems} />
      <div className="px-20 pt-5 pb-10 max-sm:px-4 max-sm:text-xs max-sm:py-4">
        {content?.content_heading && (
          <h1 className="mb-5 text-2xl">{content?.content_heading}</h1>
        )}
        <div
          dangerouslySetInnerHTML={{ __html: buildCmsHtml(content?.content) }}
        />
      </div>
    </div>
  );
};

export default ContentPage;

export async function getServerSideProps({ locale, req, res, query }) {
  const { cms_url } = query;

  // Al Manea Safety Program (Extended Warranty pricing) is an FE-built page that
  // reuses this CMS route's URL; short-circuit before the Magento CMS fetch.
  if (cms_url === "al-manea-safety-program") {
    return {
      props: {
        safetyProgram: true,
        content: null,
        ...(await serverSideTranslations(locale, ["common"])),
      },
    };
  }

  const handshake =
    req.cookies[handshakeCookieName(locale)] ||
    (await localeHandShake(locale, { req, res }))?.data?.token;
  const handshakeToken = req.cookies[handshakeCookieName(locale)] || handshake;

  const { data } = await getcmsPageByUrl(handshakeToken, cms_url);
  if (!data?.items?.length) {
    return {
      notFound: true,
    };
  }

  const isTawuniyaTerms = cms_url === "al-manea-tawuniya-terms-and-conditions";
  const content = isTawuniyaTerms ? pickCmsItemForLocale(data?.items, locale) : data?.items?.at(0);

  // The Tawuniya T&C page is CMS-authored but needs a different layout from the
  // default CMS page (full-width, no breadcrumb). Its BE HTML carries its own
  // lang switcher + tabs, wired client-side by setupCmsEvents.
  return {
    props: {
      content,
      tawuniyaTerms: isTawuniyaTerms,
      ...(await serverSideTranslations(locale, ["common"])),
    },
  };
}
